import { SubmitButton, Tooltip } from "@xola/ui-kit";
import { isExperienceProduct } from "common";
import { useFormikContext } from "formik";
import { isEmpty, uniq } from "lodash";
import React, { useEffect } from "react";
import { PaymentLineItemStatus, PaymentMethod, PurchaseItemObject } from "types";
import shallow from "zustand/shallow";
import { CreatePurchaseStep, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { Currency } from "../Common/Currency";
import { CheckoutFormValues } from "./CheckoutPurchaseScreen.types";
import { useCheckoutPurchaseScreenDisabledReasons } from "./hooks/useCheckoutPurchaseScreenDisabledReasons";
import { useGiftDetailsRequirements } from "./hooks/useGiftDetailsRequirements";
import { isReservePurchaseProcess } from "@/models/CreatePurchaseState";
import { isCreditCardRequired } from "@/models/Experience";
import { getRecipientRules } from "@/models/GiftPreference";
import { isGift } from "@/models/Purchase";

export interface CheckoutSubmitButtonProps {
    isLoading?: boolean;
    isSuccess?: boolean;
}

export const CheckoutSubmitButton = ({
    isLoading: isSubmittingPurchase = false,
    isSuccess = false,
}: CheckoutSubmitButtonProps) => {
    useCreatePurchaseStore((state) => state.purchase);
    const { recipientDetailsRequirement } = useGiftDetailsRequirements();

    const { validateForm, handleSubmit } = useFormikContext<CheckoutFormValues>();
    const isFetchingPreviewPurchase = useCreatePurchaseStore((state) => state.isFetchingPreviewPurchase);
    const isSubmitting = useCreatePurchaseStore((state) => state.isSubmitting);

    const isLoading = isFetchingPreviewPurchase || isSubmittingPurchase || isSubmitting;

    const setActiveItemIndex = useCreatePurchaseStore((state) => state.actions.setActiveItemIndex);

    const [isGiftItem, isMist] = useCreatePurchaseStore(
        (state) => [isGift(state.getActiveItem()), state.isMist()],
        shallow,
    );

    const [paymentStatus, paymentMethod] = useCreatePurchaseStore(
        (state) => [state.paymentLineItem.status, state.paymentLineItem?.payment?.method],
        shallow,
    );

    const [purchaseBalance, purchaseDueNow, purchaseAmount, purchaseId, isSplitPay] = useCreatePurchaseStore(
        (state) => {
            return [
                state.balance,
                state.dueNow ?? 0,
                state.amount,
                state.purchaseId,
                state.purchase.splitPayment?.enabled,
            ];
        },
        shallow,
    );

    const product = useCreatePurchaseStore((state) => state.getActiveProduct());

    const IsBalanceDueZero = isGiftItem ? purchaseDueNow === 0 : purchaseBalance === 0;

    const disabledReasons = useCheckoutPurchaseScreenDisabledReasons();

    const shouldDisableFooter = useCreatePurchaseStore((state) => !state.purchaseId);

    const shouldCollectCC =
        !!purchaseId && purchaseAmount === 0 && isExperienceProduct(product) && isCreditCardRequired(product);

    useEffect(() => {
        if (shouldCollectCC) {
            useCreatePurchaseStore.getState().actions.setPaymentStatus(PaymentLineItemStatus.PENDING);
        }
    }, [shouldCollectCC]);

    const isReserveProcess = isReservePurchaseProcess(paymentMethod, purchaseDueNow, paymentStatus);

    // This logic work in the next way: when you click on the button Pay, we will look if there is a gift in the purchase with
    // incorrect validation and if there is, then automatically switch to this gift and
    // scroll to the field with the error.
    const handleSubmitClick = async () => {
        const errors = await validateForm();
        const isErrorsEmpty = isEmpty(errors);
        const { purchase } = useCreatePurchaseStore.getState();
        const activeItem = useCreatePurchaseStore.getState().getActiveItem();

        const gifts = purchase.items.filter(
            (item) => item.object === PurchaseItemObject.GIFT && item.id !== activeItem.id,
        );
        const firstUnfilledGift = gifts.find(
            (gift) =>
                getRecipientRules(recipientDetailsRequirement, gift).isRecipientRequired &&
                (!gift.recipientEmail || !gift.recipientName),
        );

        if (isMist && firstUnfilledGift && isErrorsEmpty) {
            const giftIndex = purchase.items.findIndex((item) => item.id === firstUnfilledGift.id);
            setActiveItemIndex(giftIndex);

            useCreatePurchaseStore.setState({
                step: CreatePurchaseStep.GIFT_DETAILS_INVALID,
            });
            return;
        }

        handleSubmit();
    };

    return (
        <Tooltip
            content={uniq(disabledReasons).map((message) => (
                <div key={message}>{message}</div>
            ))}
            disabled={disabledReasons.length === 0}
        >
            <SubmitButton
                type="button"
                isLoading={isLoading}
                disabled={shouldDisableFooter || disabledReasons.length > 0}
                data-testid="checkout-purchase-screen-submit-button"
                color="success"
                isSuccess={isSuccess}
                onClick={handleSubmitClick}
            >
                {paymentMethod === PaymentMethod.INVOICE && !IsBalanceDueZero ? (
                    "Send Invoice"
                ) : isReserveProcess && !shouldCollectCC ? (
                    "Reserve"
                ) : isSplitPay ? (
                    "Proceed to Payment"
                ) : (
                    <>
                        <span className="mr-1">Pay</span>
                        <Currency shouldRemoveTrailingZeroes={false}>{IsBalanceDueZero ? 0 : purchaseDueNow}</Currency>
                    </>
                )}
            </SubmitButton>
        </Tooltip>
    );
};
